import React, { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { useQuery } from '@apollo/react-hooks'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import SERVICE_MESSAGES_QUERY from '../../../graphql/queries/service-messages.gql'
import { dismissServiceMessage } from '../../actions'
import useElementHeight from '../../hooks/use-element-height'
import closeImg from '../../../images/banner-close.svg'
import styles from './service-message.css'

const selectActiveServiceMessage = (
  serviceMessages = [],
  dismissedMessages = {}
) => serviceMessages.find(serviceMessage => {
  const dismissedMessage = dismissedMessages[serviceMessage.id]

  return !dismissedMessage || dismissedMessage.updatedAt < serviceMessage.updatedAt
})

/**
 * Displays the first active service message, refetching messages after route
 * changes and keeping the banner height aligned with its content.
 */
function ServiceMessages() {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const previousPathname = useRef(pathname)
  const maintenanceMode = useSelector(state => state.maintenance.mode)
  const welcomeBackBannerHeight = useSelector(state => state.ui.welcomeBackBannerHeight)
  const dismissedMessages = useSelector(
    state => state.serviceMessages.dismissed || {}
  )
  const {
    data,
    error,
    refetch
  } = useQuery(SERVICE_MESSAGES_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip: maintenanceMode
  })
  const serviceMessages = maintenanceMode
    ? []
    : data?.serviceMessages ?? []
  const serviceMessage = selectActiveServiceMessage(
    serviceMessages,
    dismissedMessages
  )
  const {
    elementHeight,
    registerHeightWatcher,
    updateElementHeight
  } = useElementHeight()

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== pathname
    previousPathname.current = pathname

    if (pathnameChanged && !maintenanceMode) {
      refetch()
    }
  }, [maintenanceMode, pathname, refetch])

  useEffect(() => {
    updateElementHeight()
  }, [serviceMessage?.message, updateElementHeight])

  if (maintenanceMode || error || !serviceMessage) {
    return null
  }

  const handleDismiss = () => {
    dispatch(dismissServiceMessage(serviceMessage))
  }

  return (
    <div>
      <div
        className={classNames(styles.wrapper, {
          [styles.belowWelcomeBackBanner]: welcomeBackBannerHeight > 0
        })}
        style={{
          height: elementHeight + 10, /* IE11 breaks without it, +10 padding */
          // the Welcome Back banner sits above the nav and pushes it down
          marginTop: welcomeBackBannerHeight
        }}
      >
        <div className={styles.message} ref={registerHeightWatcher}>
          {serviceMessage.message}
        </div>
        <div
          className={styles.close}
          onClick={handleDismiss}
          style={{
            height: elementHeight + 10 /* IE11 breaks without it, +10 padding */
          }}
        >
          <div className={styles.closeMessage}>Close this message</div>
          <img className={styles.closeIcon} src={closeImg} />
        </div>
      </div>
    </div>
  )
}

export default ServiceMessages
